import type { MessageType } from '@owl/shared';
import { type BucketSize, floorToBucket, pickBucketSize } from '../../lib/buckets';

export function HealthRing({
  value,
  size = 88,
  stroke = 8,
}: {
  value: number;
  size?: number;
  stroke?: number;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, value));
  const dash = (v / 100) * c;
  const color = v >= 95 ? '#5CB377' : v >= 85 ? '#D4B15C' : v >= 70 ? '#F59E0B' : '#EF4444';
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <title>{`Health ${v}%`}</title>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke="rgba(255,255,255,0.06)"
        strokeWidth={stroke}
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={color}
        strokeWidth={stroke}
        fill="none"
        strokeDasharray={`${dash} ${c - dash}`}
        strokeDashoffset={c / 4}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function Sparkline({
  data,
  width = 80,
  height = 26,
  color = '#D4B15C',
}: {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
}) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = data.length === 1 ? width : (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return [x, y] as const;
  });
  const d = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`)
    .join(' ');
  const dFill = `${d} L${width},${height} L0,${height} Z`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="sparkline">
      <title>Log volume trend</title>
      <path d={dFill} fill={color} fillOpacity="0.12" />
      <path d={d} fill="none" stroke={color} strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

export type TimelineBucket = { bucket: string; counts: Record<string, number> };

const LEGEND: { name: string; color: string }[] = [
  { name: 'INFO', color: '#5B87CC' },
  { name: 'SUCCESS', color: '#5CB377' },
  { name: 'WARN', color: '#F59E0B' },
  { name: 'ERROR', color: '#EF4444' },
  { name: 'FATAL', color: '#DC2626' },
];

export function ActivityBars({
  timeline,
  messageTypes,
  height = 64,
  from,
  to,
  onBucketSelect,
}: {
  timeline: TimelineBucket[];
  messageTypes: Pick<MessageType, 'id' | 'name' | 'color'>[];
  height?: number;
  // Range the timeline covers. Needed to know how wide a bucket is when the user taps one.
  from?: string;
  to?: string;
  onBucketSelect?: (sel: { fromMs: number; size: BucketSize }) => void;
}) {
  if (!timeline || timeline.length === 0) {
    return (
      <div className="m-empty" style={{ padding: 16 }}>
        Sin actividad
      </div>
    );
  }
  const byId = new Map(messageTypes.map((m) => [m.id, m]));
  const totals = timeline.map((b) => Object.values(b.counts).reduce((s, n) => s + n, 0));
  const max = Math.max(1, ...totals);
  const size: BucketSize =
    from && to ? pickBucketSize(new Date(from).getTime(), new Date(to).getTime()) : 'hour';
  const selectable = Boolean(onBucketSelect);

  return (
    <>
      <div className="m-activity" style={{ height }}>
        {timeline.map((b, i) => {
          const total = totals[i] ?? 0;
          const ratio = total / max;
          const barH = ratio * height;
          const entries = Object.entries(b.counts).sort((a, c) => c[1] - a[1]);
          return (
            <div
              key={b.bucket}
              className={`bar${selectable ? ' bar-tappable' : ''}`}
              title={`${total} logs`}
              role={selectable ? 'button' : undefined}
              tabIndex={selectable ? 0 : undefined}
              onClick={
                selectable
                  ? () =>
                      onBucketSelect?.({
                        fromMs: floorToBucket(new Date(b.bucket), size).getTime(),
                        size,
                      })
                  : undefined
              }
              onKeyDown={
                selectable
                  ? (e) => {
                      if (e.key !== 'Enter' && e.key !== ' ') return;
                      e.preventDefault();
                      onBucketSelect?.({
                        fromMs: floorToBucket(new Date(b.bucket), size).getTime(),
                        size,
                      });
                    }
                  : undefined
              }
            >
              <div style={{ height: barH, display: 'flex', flexDirection: 'column-reverse' }}>
                {entries.map(([mtId, count]) => {
                  if (count === 0 || total === 0) return null;
                  const mt = byId.get(mtId);
                  const color = mt?.color || '#5B87CC';
                  const h = (count / total) * barH;
                  return <span key={mtId} style={{ height: h, background: color }} />;
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="m-activity-legend">
        {LEGEND.map((l) => (
          <span key={l.name}>
            <i style={{ background: l.color }} />
            {l.name}
          </span>
        ))}
      </div>
    </>
  );
}

export function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

export function formatPct(ratio: number | null): string {
  if (ratio === null) return '—';
  return `${Math.round(ratio * 100)}%`;
}
